import React, {useContext, useEffect, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {useParams, useNavigate} from "react-router-dom";
import {LangContext} from "../App";
import {axios} from "../axios";
import {handleImageUrl} from "../helpers";
import {Input} from "@mui/material";
import {getTranslation} from "../translations";
import Seo from "../components/Seo/Seo";

const TYPE_MAP = {
    'private-tour': { tourType: 'private',  endpoint: 'private-tour' },
    'group-tour':   { tourType: 'group',    endpoint: 'group-tour' },
    'other-tour':   { tourType: 'other',    endpoint: 'other-tour' },
    'package':      { tourType: 'package',  endpoint: 'tour-package' },
    'abroad':       { tourType: 'abroad',   endpoint: 'abroad' },
};

const BookingPage = () => {
    const id            = useParams().id;
    const type          = useParams().type;
    const date          = useParams().date;
    const price         = useParams().price;
    const langFrom      = useParams().langFrom;
    const startingTime  = useParams().startingTime;
    const guestCount    = useParams().people;
    const childCount    = useParams().children;
    const lang          = useContext(LangContext);
    const navigate      = useNavigate();
    const t             = (key) => getTranslation(lang, key);

    const { tourType, endpoint } = TYPE_MAP[type] || TYPE_MAP['private-tour'];

    const [data,setData]                  = useState();
    const [referenceCode,setReferenceCode]= useState('');
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState('');

    const [name,setName]           = useState('');
    const [surname,setSurname]     = useState('');
    const [email,setEmail]         = useState('');
    const [countryCode,setCountryCode] = useState('');
    const [phone,setPhone]         = useState('');
    const [shareMessage,setShareMessage] = useState('');

    useEffect(() => {
        axios.get(`${lang}/${endpoint}/${id}`)
            .then(res => setData(res.data))
            .catch(() => setData(null));
    }, [id, lang, endpoint]);

    useEffect(() => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let token = '';
        for (let i = 0; i < 11; i++) {
            token += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setReferenceCode(token);
    }, []);

    const handleBooking = async () => {
        if (!name || !surname || !email || !phone) {
            setError(t('pleaseFillRequired'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const bookingData = {
                tour_type: tourType,
                tour_id: parseInt(id),
                name: `${name} ${surname}`.trim(),
                email: email,
                phone: `${countryCode}${phone}`,
                date: date,
                guests: parseInt(guestCount) || 1,
                // the API uses children for the participant limits and the price; message keeps them for the admin
                children: parseInt(childCount, 10) || 0,
                message: `Reference: ${referenceCode}, Starting Time: ${startingTime}, Language: ${langFrom}, Children: ${parseInt(childCount, 10) || 0}` + (shareMessage ? `, Message: ${shareMessage}` : ''),
                // ignored by the API, which computes the price itself
                total_price: parseFloat(price) || 0
            };

            await axios.post('/api/booking', bookingData);
            alert(t('bookingSuccess'));
            navigate('/');
        } catch (err) {
            setError(t('bookingError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-1 bookingPage">
            <Seo title="Book your tour — Ayrarat Tours" noindex />
            <div className='row'>
                <h1 className='tour-title'>{data?.title}</h1>
            </div>
            <div className="row">
                <div className="col-6">
                    {handleImageUrl(data?.thumbnail) &&
                        <img width='100%' style={{borderRadius:'20px',height:'300px',objectFit:'cover',marginLeft:'5px'}} src={handleImageUrl(data?.thumbnail)} alt=""/>}
                </div>
                <div className="col-6">
                    <div className='border rounded-4 mb-3 p-3 overflow-auto' style={{maxHeight:'100%',height:'calc(50% - 8px)'}}>
                        <p className='text-primary title_primary' style={{fontSize:'17px',fontWeightL:'bold'}}>{t('knowBeforeYouGo')}</p>
                        <p className='d-flex' dangerouslySetInnerHTML={{__html:data?.travel_info}}></p>
                    </div>
                    <div className='border rounded-4 p-3 overflow-auto' style={{maxHeight:'100%',height:'calc(50% - 8px)'}}>
                        <p className='text-primary mt-2 title_primary' style={{fontSize:'17px',fontWeightL:'bold'}}>{t('whereMeetUs')}</p>
                        <ul>
                            <li>{data?.meeting_des}</li>
                            <li>{data?.meeting_text}</li>
                        </ul>
                    </div>
                </div>
            </div>
            {error && <div className="alert alert-danger mx-1">{error}</div>}
            <div className='row border rounded-4 p-3 mt-3 mb-3 mx-1'>
                <div className='col-4'>
                    <div className='d-flex flex-column py-3'>
                        <Input placeholder={t('clientReference')} readOnly value={`${t('clientReference')}: ${referenceCode}`}/>
                        <Input placeholder={t('email')} onChange={e => setEmail(e.target.value)} required/>
                        <Input placeholder={t('startingTime')} value={`${t('startingTime')}: ${startingTime}`} readOnly/>
                    </div>
                </div>
                <div className='col-4'>
                    <div className='d-flex flex-column py-3'>
                        <Input placeholder={t('name')} onChange={e => setName(e.target.value)} required/>
                        <Input placeholder={t('countryCode')} onChange={e => setCountryCode(e.target.value)} />
                        <Input placeholder={t('date')} value={`${t('date')}: ${date}`} readOnly />
                    </div>
                </div>
                <div className='col-4'>
                    <div className='d-flex flex-column py-3'>
                        <Input placeholder={t('surname')} onChange={e => setSurname(e.target.value)} required/>
                        <Input placeholder={t('phone')} onChange={e => setPhone(e.target.value)} required/>
                        <Input placeholder={t('language')} value={`${t('language')}: ${langFrom ? t(langFrom.trim().toLowerCase()) : ''}`} readOnly />
                    </div>
                </div>
                <div className='col-12'>
                    <Input
                        placeholder={t('willYouShare')}
                        value={shareMessage}
                        onChange={e => setShareMessage(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                </div>
            </div>
            <h5 className='summery_title mx-1 ms-2'>{t('summery')}</h5>
            <div className="row mt-3 border p-3 rounded-4 mx-2 mb-5">
                <div className="col-12">
                    <h5 className='mb-2'>{t('experienceCurrency')}</h5>
                    <h6 className='text-secondary mb-2 text-gold'>&#8364; {price}</h6>
                </div>
                <div className="col-8">
                    <h5 className='mb-2'>{t('howMuchCancel')}</h5>
                    <h6 className='text-secondary mb-0 text-gold mb-2'>&#8364; {price}</h6>
                    <p className='text-secondary mb-1'>{t('cancelPolicy')}</p>
                </div>
                <div className="col-4 d-flex align-items-end">
                    <button
                        className='btn btn-primary w-100'
                        onClick={handleBooking}
                        disabled={loading}
                    >
                        {loading ? t('bookingInProgress') : t('book')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookingPage;
